import { Paper } from '@mui/material'
import { PANEL_RADIUS, SHADOWS, SP, V2 } from '../../theme/tokens'

/**
 * Panel — die Flaeche. Eine API statt `Paper` + `sx`.
 *
 * @description
 *   Gemessen am 2026-09-30 mit `scripts/erfasse_bausteine.js`: 433 Vorkommen von
 *   `Paper`/`Card` in 140 Dateien, **76,4 % davon mit lokalem `sx`** — die
 *   hoechste Ueberschreibungsquote im Baum, bei gleichzeitig nur **14**
 *   Signaturen. Die Vielfalt steckte nicht in den Attributen, sondern im `sx`.
 *
 *   Die Gegenprobe sagt, warum: `Dialog` liegt bei **1,8 %**. Dort bietet MUI
 *   `maxWidth`, `fullWidth`, `fullScreen`, `anchor` — die Formen, die man braucht.
 *   `Paper` bietet `variant` und `elevation`. Wer eine Wahl hat, trifft sie ueber
 *   die API; wer keine hat, tippt. Spezifikation:
 *   `docs/forge-ux-redesign/panel-spezifikation.md`.
 *
 *   🔴 `borderRadius` IST BEWUSST KEINE EIGENSCHAFT. Gemessen schrieben 33
 *   Stellen `borderRadius: 2` (12 px), 22 benutzten `PANEL_RADIUS` (18 px) und
 *   11 die `3` (18 px) — drei tatsaechliche Radien fuer dieselbe Flaeche, und
 *   die Mehrheit beim nicht gemeinten. Ursache war eine veraltete Doku in
 *   `tokens.js` (sie nannte 30 px, das Theme erzeugt 18 px; korrigiert am
 *   2026-09-30). Ein Baustein, der seinen Radius selbst setzt, macht die Frage
 *   unmoeglich.
 *
 *   🔴 UND `mb`/`mt`/`flex` KOMMEN NICHT MIT. Zusammen ueber 40 Setzungen — aber
 *   sie sind **Layout des Aufrufers**, nicht Eigenschaft der Flaeche: derselbe
 *   Baustein steht mal in einem Stapel mit Abstand, mal in einer Zeile ohne.
 *   Sie mitzunehmen waere der Anfang des naechsten Wildwuchses, genau die
 *   Bewegung, die dieser Baustein beendet.
 *
 * @useWhen
 *   - Eine umrandete/getönte Flaeche wird gebraucht (Karte, Panel, Sektionsrahmen)
 *   - Der Innenabstand soll aus einer der vier gemessenen Stufen kommen (`pad`)
 *   - Ein Skelett soll die Geometrie der fertigen Seite vorwegnehmen
 *     (`Skeletons/IncomingInventorySkeleton.jsx`, `ProductDetailSkeleton.jsx`)
 *
 * @dontUseFor
 *   - Schwebende Overlays und Dialoge — dafür `Dialog`, `SlidePanel`, `MobileSheet`
 *   - Eine blosse Zeile ohne Flaeche — dafür `Box` (ein Panel ohne Inhalt ist Rahmen ohne Aussage)
 *   - Eine Aktionsleiste oder Werkzeugzeile — dafür `Paper`/`Toolbar` direkt, Panel bringt Radius und Schatten mit
 *
 * @related
 *   - `Paper` (MUI) — was hier ersetzt wird; `variant`/`elevation` bleiben die Rohform
 *   - `SlidePanel` — nicht-modales Overlay, andere Aufgabe als eine ruhende Flaeche
 *   - `ActivityTimeline` und `ComposeEditor` — Verbraucher, die `disablePaper` bzw. `pad="none" clip` nutzen
 *
 * @reuseHints
 *   - Weitere Skelette unter `frontend/src/Skeletons/` auf dieselben Stufen ziehen
 *   - Neue Karten-Seiten (Vendor-, PO-, Market-Lab-Ansichten) ohne eigenes `Paper`-`sx`
 *
 * @props
 *   pad       — `none` | `sm` | `card` (Vorgabe) | `md` | `lg`; die Zahlen sind gemessen, nicht gewaehlt
 *   tone      — `paper` (Vorgabe) | `wash` | `transparent`
 *   bordered  — Vorgabe `true`; `false` setzt `border: none` UND laesst die Rahmenfarbe weg
 *   shadow    — `panel` (Vorgabe) | `none`
 *   clip      — `true` setzt `overflow: hidden` (fuer runde Ecken ueber Inhaltsraendern)
 *   component — Element/Tag der Flaeche, Vorgabe `'div'`
 *   sx        — zuletzt angewandt, der Aufrufer gewinnt
 *   ...rest   — MUI-`Paper`-Durchreiche
 */

/** Vier Stufen plus „kein". Die Zahlen sind gemessen, nicht gewaehlt. */
const PAD = {
  none: 0,                              // die 20 Stellen mit `p: 0`
  sm: SP.sm,                            // die 23 mit `p: 1.5`
  card: { xs: SP.card, sm: SP.md },     // die 76 mit `p: 2` und 48 berechnete — PANEL_SX
  md: SP.md,                            // die 14 mit `p: 3`
  lg: SP.lg,                            // die 6 mit `p: 4`
}

const TON = {
  paper: 'background.paper',
  wash: V2.wash,
  transparent: 'transparent',
}

export default function Panel({
  pad = 'card',
  tone = 'paper',
  bordered = true,
  shadow = 'panel',
  clip = false,
  component = 'div',
  sx,
  children,
  ...rest
}) {
  return (
    <Paper
      component={component}
      elevation={0}
      sx={{
        p: PAD[pad] ?? PAD.card,
        borderRadius: PANEL_RADIUS,
        // Die Rahmenfarbe nur MIT Rahmen setzen. Sonst bleibt eine verwaiste
        // Farbe stehen — jsdom zeigte sie als `border="rgba(0,0,0,0.12)"` bei
        // `borderStyle=""`, also einen Rahmen, den es nicht gibt.
        ...(bordered ? { border: '1px solid', borderColor: 'divider' } : { border: 'none' }),
        boxShadow: shadow === 'panel' ? SHADOWS.panel : 'none',
        bgcolor: TON[tone] ?? TON.paper,
        ...(clip ? { overflow: 'hidden' } : null),
        // Zuletzt, damit der Aufrufer gewinnt — er kennt den Einzelfall.
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Paper>
  )
}
